import React, { useState, useEffect } from "react";
import axios from "axios";
import { Trash, AddIcon, EditIcon, TrashWhite } from "../../assets/icons/icons";
import Pagination from "../section/Pagination";
import CustomAlert from "../CustomAlert";

export default function EditBrand() {
    const [brands, setBrands] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] = useState(false);
    const [alert, setAlert] = useState(null);
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [brandUpdated, setBrandUpdated] = useState(false);
    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (token) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        }
      }, []);
    useEffect(() => {
        axios.get("https://laravel-api.aj-mart.com/api/brand")
            .then(response => {
                setBrands(response.data.brands);
            })
            .catch(error => {
                console.error("There was an error fetching the brands:", error);
                setAlert({ type: 'error', message: 'Error fetching brands' });
            });
    }, [brandUpdated]); // Dependency on brandUpdated
    
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData();
        formData.append('brand_name', event.target.brand_name.value);
        if (image) {
            formData.append('brand_img', image);
        }

        try {
            const response = await axios.post('https://laravel-api.aj-mart.com/api/brand/add', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setBrands([...brands, response.data.brand]);
            setAlert({ type: 'success', message: 'Brand added successfully' });
            setImage(null);
            setPreview(null);
            event.target.reset();
        } catch (error) {
            setAlert({ type: 'error', message: 'Error adding brand' });
            console.error("Error adding brand:", error);
        }
    };

    const handleEditClick = (data) => {
        setEditData(data);
        setShowModal(true);
        if (data.brand_img) {
            setPreview(`https://laravel-api.aj-mart.com/storage/${data.brand_img}`);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditData({});
        setPreview(null);
        setImage(null);
    };
    const handleSaveChanges = async () => {
        if (!editData || !editData.brand_id) {
            setAlert({ type: "error", message: "No brand selected for updating." });
            return;
        }
    
        const formData = new FormData();
        formData.append("brand_name", editData.brand_name);
    
        if (image) {
            formData.append("brand_img", image);
        }
    
        try {
            const response = await axios.post(
                `https://laravel-api.aj-mart.com/api/brand/update/${editData.brand_id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "X-HTTP-Method-Override": "PUT", // Spoofing PUT for Laravel
                    },
                }
            );
    
            const data = response.data;
    
            if (data.success || data.message === "Brand updated successfully") {
                setAlert({ type: "success", message: "Brand updated successfully" });
    
                // ✅ Toggle brandUpdated to trigger refresh
                setBrandUpdated(prev => !prev);
    
                // ✅ Reset form and close modal
                handleCloseModal();
                setPreview(null);
                setImage(null);
            } else {
                let errorMessage = "Error updating brand";
                if (data.errors) {
                    errorMessage += "\n" + Object.values(data.errors).flat().join("\n");
                } else if (data.message) {
                    errorMessage = data.message;
                }
                setAlert({ type: "error", message: errorMessage });
            }
        } catch (error) {
            console.error("Error updating brand:", error);
            setAlert({ type: "error", message: "An unexpected error occurred." });
        }
    };
    
    
    const handleSelectBrand = (brand_id) => {
        setSelectedBrands(prevSelected =>
            prevSelected.includes(brand_id)
                ? prevSelected.filter(id => id !== brand_id)
                : [...prevSelected, brand_id]
        );
    };

    const handleSelectAll = () => {
        if (selectedBrands.length === brands.length) {
            setSelectedBrands([]);
        } else {
            setSelectedBrands(brands.map(brand => brand.brand_id));
        }
    };

    const handleDeleteBrand = (brand_id) => {
        setShowDeleteDialog({ brand_id });
    };

    const confirmDeleteBrand = async (brand_id) => {
        try {
            await axios.delete(`https://laravel-api.aj-mart.com/api/brand/delete/${brand_id}`);
            setBrands(brands.filter(brand => brand.brand_id !== brand_id));
            setAlert({ type: 'success', message: 'Brand deleted successfully' });
            setShowDeleteDialog(false);
        } catch (error) {
            setAlert({ type: 'error', message: 'Error deleting brand' });
            console.error("Error deleting brand:", error);
            setShowDeleteDialog(false);
        }
    };

    const handleDeleteSelectedBrands = () => {
        if (selectedBrands.length === 0) {
            setAlert({ type: 'error', message: 'Please select at least one brand to delete' });
            return;
        }
        setShowDeleteSelectedDialog(true);
    };

    const confirmDeleteSelectedBrands = async () => {
        try {
            await Promise.all(selectedBrands.map(brand_id => 
                axios.delete(`https://laravel-api.aj-mart.com/api/brand/delete/${brand_id}`)
            ));
            setBrands(brands.filter(brand => !selectedBrands.includes(brand.brand_id)));
            setAlert({ type: 'success', message: 'Selected brands deleted successfully' });
            setSelectedBrands([]);
            setShowDeleteSelectedDialog(false);
        } catch (error) {
            setAlert({ type: 'error', message: 'Error deleting brands' });
            console.error("Error deleting brands:", error);
            setShowDeleteSelectedDialog(false);
        }
    };

    return (
        <section className="py-[25px]">
            {alert && <CustomAlert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

            <div className="main-title mb-[20px]">Manage Brands</div>
            
            <div className="mb-[30px]">
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-[10px]">
                        <div className="flex items-center gap-[10px]">
                            <div className="flex flex-col gap-[5px] w-[49%]">
                                <label className="text-black">Brand Name</label>
                                <input
                                    name="brand_name"
                                    type="text"
                                    className="px-[15px] py-[7px] outline-none border-2 border-[#1485a6] rounded-[4px] text-black bg-[#eeeeee6e]"
                                    placeholder="Brand Name"
                                    required
                                />
                            </div>

                            <div className="flex flex-col gap-[5px] w-[49%]">
                                <label className="text-black">Brand Image</label>
                                <div className="flex items-center gap-[10px]">
                                    <label
                                        htmlFor="brand-image"
                                        className="cursor-pointer border-2 border-[#1485a6] bg-[#eeeeee6e] text-black px-[15px] py-[7px] rounded-[4px] flex items-center justify-center"
                                    >
                                        {preview ? (
                                            <img
                                                src={preview}
                                                alt="Preview"
                                                className="w-[40px] h-[40px] object-cover rounded"
                                            />
                                        ) : (
                                            "Upload Image"
                                        )}
                                    </label>
                                    <input
                                        id="brand-image"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <button type="submit" className="btn flex items-center gap-[5px]">
                            <AddIcon />
                            <span>Add Brand</span>
                        </button>
                    </div>
                </form>
            </div>

            <div className="flex justify-end items-center gap-[10px] capitalize mb-[20px]">
                <button
                    className="btn bg-red-500 hover:bg-red-600 flex items-center gap-2"
                    onClick={handleDeleteSelectedBrands}
                >
                    <TrashWhite />
                    <span>Delete Selected Brands</span>
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 text-left">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border border-gray-200 p-[10px] capitalize">
                                <input
                                    type="checkbox"
className="w-[18px] h-[18px]"                                    checked={selectedBrands.length === brands.length}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th className="border border-gray-200 p-[10px] capitalize">Image</th>
                            <th className="border border-gray-200 p-[10px] capitalize">Brand Name</th>
                            <th className="border border-gray-200 p-[10px] capitalize">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {brands.map(brand => (
                            <tr key={brand.brand_id} className="bg-white hover:bg-gray-50">
                                <td className="border border-gray-200 p-[10px]">
                                    <input
                                        type="checkbox"
className="w-[18px] h-[18px]"                                        checked={selectedBrands.includes(brand.brand_id)}
                                        onChange={() => handleSelectBrand(brand.brand_id)}
                                    />
                                </td>
                                <td className="border border-gray-200 p-[10px]">
                                    <img
                                        src={`https://laravel-api.aj-mart.com/storage/app/public/${brand.brand_img}`}
                                        alt={brand.brand_name}
                                        className="w-[50px] h-[50px] object-cover"
                                    />
                                </td>
                                <td className="border border-gray-200 p-[10px]">{brand.brand_name}</td>
                                <td className="border border-gray-200 p-[10px]">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="cursor-pointer"
                                            onClick={() => handleEditClick(brand)}
                                        >
                                            <EditIcon />
                                        </div>
                                        <div
                                            className="cursor-pointer"
                                            onClick={() => handleDeleteBrand(brand.brand_id)}
                                        >
                                            <Trash />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="py-[25px]">
                <Pagination />
            </div>

            {/* Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white w-[500px] rounded-lg shadow-lg p-[20px] relative">
                        <h2 className="text-xl font-bold mb-[10px]">Edit Brand</h2>
                        <div className="flex flex-col gap-[10px] capitalize">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-1/2">
                                    <label className="text-sm font-medium">Current Image</label>
                                    {editData.brand_img && (
                                        <img
                                            src={`https://laravel-api.aj-mart.com/storage/app/public/${editData.brand_img}`}
                                            alt="Current"
                                            className="w-[100px] h-[100px] object-cover mt-2"
                                        />
                                    )}
                                </div>
                                <div className="w-1/2">
                                    <label className="text-sm font-medium">New Image</label>
                                    <div className="mt-2">
                                        <label
                                            htmlFor="edit-brand-image"
                                            className="cursor-pointer border-2 border-[#1485a6] bg-[#eeeeee6e] text-black px-[15px] py-[7px] rounded-[4px] flex items-center justify-center"
                                        >
                                            {preview ? (
                                                <img
                                                    src={preview}
                                                    alt="Preview"
                                                    className="w-[40px] h-[40px] object-cover rounded"
                                                />
                                            ) : (
                                                "Change Image"
                                            )}
                                        </label>
                                        <input
                                            id="edit-brand-image"
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <label className="text-sm font-medium">
                                Brand Name
                                <input
                                    type="text"
                                    className="block w-full mt-1 p-[10px] capitalize border rounded"
                                    value={editData.brand_name}
                                    onChange={(e) => setEditData({ ...editData, brand_name: e.target.value })}
                                    required
                                />
                            </label>
                        </div>
                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                className="btn bg-gray-300 hover:bg-gray-400"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn bg-blue-500 hover:bg-blue-600"
                                onClick={handleSaveChanges}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white w-[400px] rounded-lg shadow-lg p-[20px] relative">
                        <h2 className="text-xl font-bold mb-[10px]">Confirm Deletion</h2>
                        <p className="text-sm mb-[20px]">
                            Are you sure you want to delete this brand?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                className="btn bg-gray-300 hover:bg-gray-400"
                                onClick={() => setShowDeleteDialog(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn bg-red-500 hover:bg-red-600"
                                onClick={() => confirmDeleteBrand(showDeleteDialog.brand_id)}
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Selected Confirmation Dialog */}
            {showDeleteSelectedDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white w-[400px] rounded-lg shadow-lg p-[20px] relative">
                        <h2 className="text-xl font-bold mb-[10px]">Confirm Deletion</h2>
                        <p className="text-sm mb-[20px]">
                            Are you sure you want to delete the selected brands?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                className="btn bg-gray-300 hover:bg-gray-400"
                                onClick={() => setShowDeleteSelectedDialog(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn bg-red-500 hover:bg-red-600"
                                onClick={confirmDeleteSelectedBrands}
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}